import React, { useState, useMemo } from 'react';
import { Save, Plus, Edit2, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { apiCall, useAdminData } from './_helpers';

const MenuManager = ({ onMessage }) => {
  const [items, setItems, refetch] = useAdminData('menu-items');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);
  const [newCategoryFor, setNewCategoryFor] = useState(null);

  // Derive categories dynamically from the actual items.
  // Order each category by the lowest section_order found in that group so
  // sections stay in their intended display order.
  const groupedCategories = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      const cat = it.category || 'Uncategorized';
      const existing = map.get(cat);
      const so = it.section_order ?? 999;
      if (!existing || so < existing.section_order) {
        map.set(cat, { name: cat, section_order: so });
      }
    });
    return Array.from(map.values()).sort(
      (a, b) => a.section_order - b.section_order || a.name.localeCompare(b.name)
    );
  }, [items]);

  const allCategoryNames = groupedCategories.map((c) => c.name);

  const handleSave = async (item, isNew) => {
    try {
      let payload = item;
      if (isNew && (!payload.item_order || payload.item_order === 0)) {
        // Put new items at the bottom of their section by default.
        const max = items
          .filter((i) => (i.category || 'Uncategorized') === (payload.category || 'Uncategorized'))
          .reduce((m, i) => Math.max(m, i.item_order ?? 0), -1);
        payload = { ...payload, item_order: max + 1 };
      }
      if (isNew) await apiCall('POST', 'menu-items', payload);
      else await apiCall('PUT', `menu-items/${payload._id}`, payload);
      onMessage('success', 'Saved');
      setCreating(null);
      setEditing(null);
      refetch();
    } catch (e) {
      onMessage('error', e.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await apiCall('DELETE', `menu-items/${id}`);
      onMessage('success', 'Deleted');
      refetch();
    } catch (e) {
      onMessage('error', e.message);
    }
  };

  // Swap item_order between two items (used by up/down arrows). Persists both
  // changes via PUT calls then refetches, so the new order is reflected
  // immediately in both the admin and the public site.
  const handleMove = async (category, item, direction) => {
    const list = items
      .filter((i) => (i.category || 'Uncategorized') === category)
      .sort((a, b) => (a.item_order ?? 0) - (b.item_order ?? 0));
    const idx = list.findIndex((i) => i._id === item._id);
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= list.length) return;
    const a = list[idx];
    const b = list[target];
    const aOrder = a.item_order ?? idx;
    const bOrder = b.item_order ?? target;
    try {
      await Promise.all([
        apiCall('PUT', `menu-items/${a._id}`, { item_order: bOrder }),
        apiCall('PUT', `menu-items/${b._id}`, { item_order: aOrder }),
      ]);
      refetch();
    } catch (e) {
      onMessage('error', e.message);
    }
  };

  const Form = ({ value, onChange, onSubmit, onCancel }) => {
    const [addingNew, setAddingNew] = useState(false);
    return (
      <div className="bg-white rounded-xl p-6 shadow-md mb-4 border-l-4 border-[#C9A86C]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            type="text"
            placeholder="Name"
            value={value.name || ''}
            onChange={(e) => onChange({ ...value, name: e.target.value })}
            className="border rounded px-3 py-2"
            data-testid="menuitem-name-input"
          />
          {addingNew ? (
            <input
              type="text"
              placeholder="New category name"
              value={value.category || ''}
              onChange={(e) => onChange({ ...value, category: e.target.value })}
              className="border rounded px-3 py-2"
              autoFocus
            />
          ) : (
            <div className="flex gap-2">
              <select
                value={value.category || allCategoryNames[0] || ''}
                onChange={(e) => {
                  if (e.target.value === '__new__') setAddingNew(true);
                  else onChange({ ...value, category: e.target.value });
                }}
                className="border rounded px-3 py-2 flex-1"
                data-testid="menuitem-category-select"
              >
                {allCategoryNames.map((c) => <option key={c} value={c}>{c}</option>)}
                <option value="__new__">+ New category…</option>
              </select>
            </div>
          )}
          <textarea
            placeholder="Description"
            value={value.description || ''}
            onChange={(e) => onChange({ ...value, description: e.target.value })}
            className="border rounded px-3 py-2 md:col-span-2"
            rows={2}
          />
          <input
            type="text"
            placeholder="Special tag (optional, e.g. Chef's Pick)"
            value={value.special || ''}
            onChange={(e) => onChange({ ...value, special: e.target.value })}
            className="border rounded px-3 py-2"
          />
          <input
            type="text"
            placeholder="Price (e.g. 24 or MP)"
            value={value.price || ''}
            onChange={(e) => onChange({ ...value, price: e.target.value })}
            className="border rounded px-3 py-2"
            data-testid="menuitem-price-input"
          />
          <input
            type="text"
            placeholder="Sub-header (e.g. Bubbles, White, Red — wines only)"
            value={value.sub_header || ''}
            onChange={(e) => onChange({ ...value, sub_header: e.target.value })}
            className="border rounded px-3 py-2 md:col-span-2"
            data-testid="menuitem-subheader-input"
          />
          <input
            type="number"
            placeholder="Order within section"
            value={value.item_order ?? 0}
            onChange={(e) => onChange({ ...value, item_order: parseInt(e.target.value || '0', 10) })}
            className="border rounded px-3 py-2"
          />
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <input
              type="checkbox"
              checked={value.is_active !== false}
              onChange={(e) => onChange({ ...value, is_active: e.target.checked })}
            />
            Active (show on the menu page)
          </label>
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={onSubmit} className="bg-green-600 text-white px-4 py-2 rounded flex items-center gap-1" data-testid="menuitem-save-btn">
            <Save size={16} /> Save
          </button>
          <button onClick={onCancel} className="bg-gray-500 text-white px-4 py-2 rounded">Cancel</button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Menu Items</h2>
          <p className="text-sm text-gray-500 mt-1">
            {items.length} item{items.length === 1 ? '' : 's'} across {groupedCategories.length} section{groupedCategories.length === 1 ? '' : 's'}.
            Edit any item below or add a new one. Use "+ New category…" in the dropdown to create a new section.
          </p>
        </div>
        <button
          onClick={() =>
            setCreating({
              name: '',
              description: '',
              category: allCategoryNames[0] || '',
              special: '',
              price: '',
              is_active: true,
              item_order: 0,
            })
          }
          className="bg-[#C9A86C] text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#B8956A]"
          data-testid="add-menuitem-btn"
        >
          <Plus size={18} /> Add Item
        </button>
      </div>

      {creating && (
        <Form
          value={creating}
          onChange={setCreating}
          onSubmit={() => handleSave(creating, true)}
          onCancel={() => setCreating(null)}
        />
      )}

      {groupedCategories.length === 0 && !creating && (
        <div className="bg-white rounded-xl p-12 shadow-md text-center text-gray-500">
          No menu items yet. Click "Add Item" to create your first dish.
        </div>
      )}

      {groupedCategories.map(({ name: category }) => {
        const list = items
          .filter((i) => (i.category || 'Uncategorized') === category)
          .sort((a, b) => (a.item_order ?? 0) - (b.item_order ?? 0));
        return (
          <div key={category} className="mb-8" data-testid={`menu-category-${category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
            <h3 className="text-xl font-semibold mb-3 text-[#C9A86C]">
              {category}
              <span className="ml-2 text-sm text-gray-400 font-normal">({list.length})</span>
            </h3>
            <div className="space-y-3">
              {list.map((item, idx) =>
                editing && editing._id === item._id ? (
                  <Form
                    key={item._id}
                    value={editing}
                    onChange={setEditing}
                    onSubmit={() => handleSave(editing, false)}
                    onCancel={() => setEditing(null)}
                  />
                ) : (
                  <div
                    key={item._id}
                    className="bg-white rounded-lg p-4 shadow-sm flex justify-between items-start"
                    data-testid={`menu-item-${item._id}`}
                  >
                    <div className="flex flex-col items-center mr-3 -mt-1">
                      <button
                        onClick={() => handleMove(category, item, 'up')}
                        disabled={idx === 0}
                        className="text-gray-500 hover:bg-gray-100 disabled:opacity-25 disabled:cursor-not-allowed p-1 rounded"
                        title="Move up"
                        data-testid={`move-up-${item._id}`}
                      >
                        <ChevronUp size={18} />
                      </button>
                      <span className="text-[10px] text-gray-400 leading-none">{idx + 1}</span>
                      <button
                        onClick={() => handleMove(category, item, 'down')}
                        disabled={idx === list.length - 1}
                        className="text-gray-500 hover:bg-gray-100 disabled:opacity-25 disabled:cursor-not-allowed p-1 rounded"
                        title="Move down"
                        data-testid={`move-down-${item._id}`}
                      >
                        <ChevronDown size={18} />
                      </button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline gap-3">
                        <h4 className="font-semibold">
                          {item.name}
                          {item.is_active === false && (
                            <span className="ml-2 text-xs font-normal text-gray-400 bg-gray-100 px-2 py-0.5 rounded">Hidden</span>
                          )}
                        </h4>
                        {item.price && (
                          <span className="text-sm font-medium text-gray-700 tabular-nums whitespace-nowrap">
                            {item.price}
                          </span>
                        )}
                      </div>
                      {item.description && (
                        <p className="text-gray-600 text-sm mt-1">{item.description}</p>
                      )}
                      {item.special && (
                        <span className="inline-block mt-2 text-xs bg-[#C9A86C] text-white px-2 py-1 rounded">{item.special}</span>
                      )}
                    </div>
                    <div className="flex gap-2 ml-3">
                      <button onClick={() => setEditing({ ...item })} className="text-blue-600 hover:bg-blue-50 p-2 rounded" data-testid={`edit-menuitem-${item._id}`}>
                        <Edit2 size={18} />
                      </button>
                      <button onClick={() => handleDelete(item._id)} className="text-red-600 hover:bg-red-50 p-2 rounded" data-testid={`delete-menuitem-${item._id}`}>
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MenuManager;
